import { useEffect, useRef, useState } from "react";
import { initializeB24Frame } from "@bitrix24/b24jssdk";
import logoIconLight from "@/assets/logo-icon-light.png";
import logoIconDark from "@/assets/logo-icon-dark.png";
import {
  runInstallRoutine,
  type StepStatus,
  type StepId,
  type AppState,
  type ErrorInfo,
} from "@/lib/install-routine";
import {
  SELECTABLE_STORAGE_REGIONS,
  DEFAULT_STORAGE_REGION,
  storageConfig,
  type StorageRegion,
} from "@shared";

interface StepDef {
  id: StepId;
  label: string;
  activeDetail: string;
}

// Data Storage Location — install-time picker copy. Deliberately not run
// through i18n: this page has no i18n provider (matches the rest of
// install.tsx's standalone-entry convention).
const REGION_LABELS: Record<StorageRegion, { name: string; detail: string }> = {
  LEGACY: { name: "Default", detail: "" }, // never shown — hidden in the registry
  ENAM: { name: "Eastern North America", detail: "US East, Canada East" },
  WNAM: { name: "Western North America", detail: "US West, Canada West" },
  WEUR: { name: "Western Europe", detail: "UK, France, Germany, Spain" },
  EEUR: { name: "Eastern Europe", detail: "Poland, Romania, Ukraine" },
  APAC: { name: "Asia-Pacific", detail: "Singapore, Japan, India" },
  OC: { name: "Oceania", detail: "Australia, New Zealand" },
  EU: { name: "European Union", detail: "GDPR-grade — data never leaves the EU" },
};

const STEP_DEFS: StepDef[] = [
  {
    id: "connect",
    label: "Connecting to Bitrix24",
    activeDetail: "Establishing a secure connection with your portal…",
  },
  {
    id: "workspace",
    label: "Preparing workspace",
    activeDetail: "Setting up your data entities and event handlers…",
  },
  {
    id: "credentials",
    label: "Securing credentials",
    activeDetail: "Encrypting and storing your access tokens…",
  },
  {
    id: "activating",
    label: "Activating workspace",
    activeDetail: "Finalising setup with your portal…",
  },
];

function CheckIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4m0 4h.01"
      />
    </svg>
  );
}

function StepIcon({ status }: { status: StepStatus }) {
  if (status === "done") {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-green-100 text-green-600 shrink-0">
        <CheckIcon />
      </span>
    );
  }
  if (status === "warning") {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-600 shrink-0">
        <WarnIcon />
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-100 text-red-600 shrink-0">
        <CrossIcon />
      </span>
    );
  }
  if (status === "active") {
    return (
      <span className="flex h-7 w-7 items-center justify-center shrink-0">
        <span className="h-5 w-5 animate-spin rounded-full border-[3px] border-primary border-t-transparent" />
      </span>
    );
  }
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-muted-foreground/25 shrink-0">
      <span className="h-2 w-2 rounded-full bg-muted-foreground/25" />
    </span>
  );
}

function RegionOption({
  region,
  selected,
  onSelect,
}: {
  region: StorageRegion;
  selected: boolean;
  onSelect: () => void;
}) {
  const label = REGION_LABELS[region];
  return (
    <button
      type="button"
      onClick={onSelect}
      className={
        "w-full flex items-center gap-3 rounded-lg border p-2.5 text-left transition-colors " +
        (selected
          ? "border-primary bg-primary/5"
          : "border-border hover:border-muted-foreground/40")
      }
    >
      <span
        className={
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 " +
          (selected ? "border-primary" : "border-muted-foreground/30")
        }
      >
        {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{label.name}</span>
        <span className="block text-xs text-muted-foreground">{label.detail}</span>
      </span>
    </button>
  );
}

interface InstallFlowProps {
  mode?: "install" | "repair";
  onComplete?: () => void;
  // If b24 is already initialized (e.g. during repair mode in BitrixProvider), pass it in.
  // Otherwise, the flow will initialize it.
  b24Instance?: Awaited<ReturnType<typeof initializeB24Frame>> | null;
}

export function InstallFlow({ mode = "install", onComplete, b24Instance }: InstallFlowProps) {
  const [statuses, setStatuses] = useState<Record<StepId, StepStatus>>({
    connect: "pending",
    workspace: "pending",
    credentials: "pending",
    activating: "pending",
  });
  const [appState, setAppState] = useState<AppState>("running");
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [warnMsg, setWarnMsg] = useState("");

  // Data Storage Location: blocks provisioning until the admin picks a
  // region — install mode only, never repair (repair means the tenant
  // already made this choice; re-asking would be confusing and pointless
  // since the choice is immutable anyway). Deliberately starts `false`,
  // not `mode === "install"` — flipping it true only after a SUCCESSFUL
  // connect. Starting it true would show the picker even when the B24
  // connection itself fails (e.g. opened outside a real iframe), stuck
  // forever since nothing would ever set it back to false on that path.
  const [awaitingRegionChoice, setAwaitingRegionChoice] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<StorageRegion>(DEFAULT_STORAGE_REGION);
  const connectedB24Ref = useRef<Awaited<ReturnType<typeof initializeB24Frame>> | null>(null);
  const cancelledRef = useRef(false);

  function setStep(id: StepId, status: StepStatus) {
    setStatuses((prev) => ({ ...prev, [id]: status }));
  }

  async function provisionWith(
    b24: Awaited<ReturnType<typeof initializeB24Frame>>,
    region?: StorageRegion,
  ) {
    await runInstallRoutine(
      b24,
      {
        setStep,
        setErrorInfo,
        setWarnMsg,
        setAppState,
        isCancelled: () => cancelledRef.current,
      },
      region,
    );

    // Automatically complete flow after a short delay if successful
    // so the parent can transition to the main app if in repair mode.
    setTimeout(() => {
      if (!cancelledRef.current && onComplete) {
        onComplete();
      }
    }, 2000);
  }

  useEffect(() => {
    cancelledRef.current = false;

    async function runInstall() {
      setStep("connect", "active");

      let b24 = b24Instance;

      if (!b24) {
        try {
          b24 = await initializeB24Frame();
        } catch (err: unknown) {
          if (cancelledRef.current) return;
          const code =
            typeof err === "object" && err !== null && "code" in err
              ? (err as Record<string, string>).code
              : "";
          if (code === "JSSDK_CLIENT_SIDE_WARNING") {
            setAppState("dev");
            return;
          }
          setStep("connect", "error");
          setErrorInfo({
            title: "Could not connect to Bitrix24",
            detail:
              "The install page must be opened inside a Bitrix24 portal iframe. " +
              "If you are a portal admin, please reinstall the app from the marketplace.",
            raw: err instanceof Error ? err.message : String(err),
          });
          setAppState("error");
          return;
        }
      }

      if (cancelledRef.current) return;
      connectedB24Ref.current = b24;

      if (mode === "install") {
        // Hold here — the region picker's Continue button starts provisioning.
        setAwaitingRegionChoice(true);
        return;
      }

      // Repair mode: never shows the picker, region is already fixed.
      await provisionWith(b24);
    }

    void runInstall();
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- provisionWith is stable per render and re-including it would re-run the connect effect on every render
  }, [b24Instance, mode, onComplete]);

  function handleRegionConfirm() {
    setAwaitingRegionChoice(false);
    const b24 = connectedB24Ref.current;
    if (!b24) return;
    void provisionWith(b24, selectedRegion);
  }

  const WEIGHT: Record<StepStatus, number> = {
    pending: 0,
    active: 0.4,
    done: 1,
    warning: 1,
    error: 0,
  };
  const progressPct = Math.round(
    (STEP_DEFS.reduce((sum, s) => sum + WEIGHT[statuses[s.id]], 0) / STEP_DEFS.length) * 100,
  );
  const activeStepDef = STEP_DEFS.find((s) => statuses[s.id] === "active");

  const titleText =
    mode === "repair" ? "Wrapping up app configurations…" : "Setting up your workspace…";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-sm w-full space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex items-center justify-center h-16 w-16 mb-2">
            <img
              src={logoIconLight}
              alt="BitCommissions24 Logo"
              className="block dark:hidden h-full w-full object-contain"
            />
            <img
              src={logoIconDark}
              alt="BitCommissions24 Logo"
              className="hidden dark:block h-full w-full object-contain"
            />
          </div>
          <h1 className="text-xl font-semibold text-foreground">BitCommissions24</h1>
          <p className="text-sm text-muted-foreground">
            {awaitingRegionChoice
              ? "Choose where your data lives"
              : appState === "done" || appState === "partial"
                ? "Your workspace is ready!"
                : appState === "error"
                  ? "Installation could not complete"
                  : titleText}
          </p>
        </div>

        {awaitingRegionChoice ? (
          <div className="space-y-4">
            <div className="rounded-xl border bg-card p-5 space-y-4">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Select the region where BitCommissions24 will store your commission rules and
                journal data.{" "}
                <span className="font-medium text-foreground">This cannot be changed later</span> —
                you can migrate to a new region only by contacting support.
              </p>

              <div className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Compliance-guaranteed
                </p>
                {SELECTABLE_STORAGE_REGIONS.filter((r) => storageConfig(r).compliance).map((r) => (
                  <RegionOption
                    key={r}
                    region={r}
                    selected={selectedRegion === r}
                    onSelect={() => {
                      setSelectedRegion(r);
                    }}
                  />
                ))}
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Performance-optimized
                </p>
                <p className="text-[11px] text-muted-foreground/70 -mt-1">
                  Best-effort placement for lower latency — not a compliance guarantee.
                </p>
                {SELECTABLE_STORAGE_REGIONS.filter((r) => !storageConfig(r).compliance).map((r) => (
                  <RegionOption
                    key={r}
                    region={r}
                    selected={selectedRegion === r}
                    onSelect={() => {
                      setSelectedRegion(r);
                    }}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={handleRegionConfirm}
              className="w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Continue with {REGION_LABELS[selectedRegion].name}
            </button>
          </div>
        ) : appState === "dev" ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm space-y-1">
            <p className="font-medium text-amber-800">Development mode</p>
            <p className="text-amber-700">
              This page only runs inside a Bitrix24 portal iframe. Open the app via the marketplace
              to test the full install flow.
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-xl border bg-card p-5 space-y-3.5">
              {STEP_DEFS.map((step, idx) => {
                const status = statuses[step.id];
                return (
                  <div key={step.id} className="flex items-center gap-3">
                    <StepIcon status={status} />
                    <div className="flex-1 min-w-0">
                      <p
                        className={
                          status === "pending"
                            ? "text-sm text-muted-foreground/60"
                            : status === "error"
                              ? "text-sm font-medium text-destructive"
                              : status === "warning"
                                ? "text-sm font-medium text-amber-700 dark:text-amber-400"
                                : status === "active"
                                  ? "text-sm font-semibold text-foreground"
                                  : "text-sm font-medium text-foreground"
                        }
                      >
                        {step.label}
                      </p>
                    </div>
                    {status === "pending" && (
                      <span className="text-xs text-muted-foreground/40 tabular-nums shrink-0">
                        {idx + 1}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {appState === "running" && (
              <div className="space-y-1.5">
                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-700 ease-out"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                {activeStepDef && (
                  <p className="text-xs text-center text-muted-foreground">
                    {activeStepDef.activeDetail}
                  </p>
                )}
              </div>
            )}

            {appState === "done" && (
              <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/40 p-4 text-center space-y-1">
                <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                  Installation complete!
                </p>
                <p className="text-xs text-green-700 dark:text-green-400">
                  {mode === "repair" ? "Refreshing app..." : "Opening Commissions Manager…"}
                </p>
              </div>
            )}

            {appState === "partial" && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 p-4 space-y-1.5">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  Installation complete (with a minor issue)
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400">{warnMsg}</p>
              </div>
            )}

            {appState === "error" && errorInfo && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-destructive">{errorInfo.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {errorInfo.detail}
                  </p>
                </div>
                {errorInfo.raw && (
                  <details>
                    <summary className="text-xs text-muted-foreground/60 cursor-pointer select-none hover:text-muted-foreground transition-colors">
                      Technical details
                    </summary>
                    <p className="mt-1.5 text-xs font-mono text-muted-foreground/70 break-all bg-muted rounded p-2">
                      {errorInfo.raw}
                    </p>
                  </details>
                )}
                <button
                  onClick={() => {
                    window.location.reload();
                  }}
                  className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Retry Configuration
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
